import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import { useIntl } from 'umi';
import { getInitialFormValueStoreRankDetail } from './utils';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 单榜单筛选器
  SINGLE_SOURCE_SELECT as SINGLE_SOURCE_SELECT_FILTER,
  STORERANK_SEGEMENT,
  REGION,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_FORM_VALUE } from './config';

import type { IBaseProps, IDateTypely, IRegion } from '@/pages/intelligence/types/common';

interface IStoreRankDetailsFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  regionsOptions: IRegion[];
  regionsOptionsMap: {
    [key: string]: IRegion[];
  };
  sources?: string[];
  source: string;
  granularity?: IDateTypely;
  startDate?: string;
  endDate?: string;
  onConfirm?: (payload: any, segmentValue: string) => void;
  onReset?: () => void;
  sinceReleaseTime?: string;
  earliestDataTime?: string;
}

const StoreRankDetailsFilter: React.FC<IStoreRankDetailsFilterProps> = (props) => {
  const {
    iconText,
    regionsOptions,
    inputList: inputListTemp,
    source,
    sources = [],
    granularity,
    startDate,
    endDate,
    onConfirm,
    onReset,
    activeSegment,
    setActiveSegment,
    sinceReleaseTime,
    earliestDataTime,
  } = props;

  const urlSearchParams = new URLSearchParams(location.search);
  const urlTopChartsName = decodeURIComponent(urlSearchParams.get('source') || '');
  // console.log('urlTopChartsName', urlTopChartsName)
  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend, resetDetailFilterExtend } =
    useFilterExtend({});

  // 筛选器的初始值
  const initialFormValue = getInitialFormValueStoreRankDetail(
    isOperateDetail,
    detailFilterExtend,
    cloneDeep({
      ...DEFAULT_FORM_VALUE,
      storerank_segement: {
        value: urlTopChartsName ? 'market' : 'rank',
      },
    }),
  );

  // console.log('initialFormValue', initialFormValue);

  // 筛选器UI渲染需要的value
  const formValue = useRef<any>(initialFormValue);

  // 动态筛选器配置，即全部或者部分配置 需要通过组件props、组件其他state、组件其他变量来决定，不能单独写在一个js文件中
  // const dynamicFilterConfigs = {
  // };

  const getInputListCur = (inputListTemp: any[], activeSegment: string) => {
    const REGIONFilterCfg = inputListTemp?.find((item) => item?.key === REGION);
    if (!REGIONFilterCfg) return;
    if (activeSegment === 'market') {
      REGIONFilterCfg.globalInfoKey = 'dbi_info_global_tips_store_rank_mobile_detail_market';
      REGIONFilterCfg.globalWoChianInfoKey =
        'dbi_info_global_wo_china_tips_store_rank_mobile_detail_market';
    } else {
      REGIONFilterCfg.globalInfoKey = 'dbi_info_global_tips_store_rank_mobile_detail';
      REGIONFilterCfg.globalWoChianInfoKey =
        'dbi_info_global_wo_china_tips_store_rank_mobile_detail';
    }
    return inputListTemp;
  };

  // 筛选器控件的配置
  const [inputList, setInputList] = useState(getInputListCur(inputListTemp, activeSegment));

  // 更新组合国家筛选器控件配置
  const updateREGIONFilterCfg = (inputList: any[], cfg: { options: IRegion[]; }) => {
    const REGIONFilterCfg = inputList?.find((item) => item?.key === REGION);
    if (!REGIONFilterCfg) return;
    const { options } = cfg || {};
    REGIONFilterCfg.options = Array.isArray(options) ? options : [];
    if (activeSegment === 'market') {
      REGIONFilterCfg.globalInfoKey = 'dbi_info_global_tips_store_rank_mobile_detail_market';
      REGIONFilterCfg.globalWoChianInfoKey =
        'dbi_info_global_wo_china_tips_store_rank_mobile_detail_market';
    } else {
      REGIONFilterCfg.globalInfoKey = 'dbi_info_global_tips_store_rank_mobile_detail';
      REGIONFilterCfg.globalWoChianInfoKey =
        'dbi_info_global_wo_china_tips_store_rank_mobile_detail';
    }
  };

  // 更新单数据源筛选器控件配置
  const updateSingleSourceFilterCfg = (inputList: any[], cfg: { sources: string[]; }) => {
    const singleSourceFilterCfg = inputList?.find(
      (item) => item?.key === SINGLE_SOURCE_SELECT_FILTER,
    );
    if (!singleSourceFilterCfg) return;
    const { sources } = cfg || {};
    singleSourceFilterCfg.sources = Array.isArray(sources) ? sources : [];
  };

  // 更新 日期筛选器配置
  const updateRangeTimeCfg = (
    inputList: any[],
    sinceReleaseTime?: string,
    earliestDataTime?: string,
  ) => {
    const filterRangeTime = inputList?.find((item) => item?.key === RANGE_TIME_SELECT_FILTER);
    if (!filterRangeTime) return;
    filterRangeTime.sinceReleaseTime = sinceReleaseTime || '';
    filterRangeTime.earliestDataTime = earliestDataTime || '';
  };

  /**
   * 更新各个筛选器的控件配置
   */
  useEffect(() => {
    const inputListTmp = [...inputList];
    updateREGIONFilterCfg(inputListTmp, { options: regionsOptions });
    setInputList(inputListTmp);
  }, [activeSegment, regionsOptions]);

  useEffect(() => {
    const inputListTmp = [...inputList];
    if (activeSegment === 'market') {
      updateSingleSourceFilterCfg(inputListTmp, { sources });
      setInputList(inputListTmp);
    }
  }, [sources, activeSegment]);

  useEffect(() => {
    const inputListTmp = [...inputList];
    // 更新 日期筛选器配置
    updateRangeTimeCfg(
      inputListTmp,
      sinceReleaseTime,
      earliestDataTime
    );
    setInputList(inputListTmp);
  }, [sinceReleaseTime, earliestDataTime]);

  useEffect(() => {
    // console.log('formValue', formValue);
    // console.log('source', source);
    if (formValue.current[SINGLE_SOURCE_SELECT_FILTER]) {
      formValue.current[SINGLE_SOURCE_SELECT_FILTER].value =
        source || DEFAULT_FORM_VALUE[SINGLE_SOURCE_SELECT_FILTER].value;
    }
  }, [source]);

  /**
   * 同步父组件传入的时间
   */
  useEffect(() => {
    const rangeTimeValue = formValue.current?.[RANGE_TIME_SELECT_FILTER];
    formValue.current = {
      ...formValue.current,
      [RANGE_TIME_SELECT_FILTER]: {
        type: granularity
          ? feachTimeTypeToFilterTimeType(granularity)
          : rangeTimeValue?.type || DEFAULT_FORM_VALUE[RANGE_TIME_SELECT_FILTER].type,
        value:
          startDate && endDate ? [moment(startDate), moment(endDate)] : rangeTimeValue?.value || [],
      },
    };
  }, [granularity, startDate, endDate]);

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    // console.log('handleConfirm-value', value);
    // console.log('handleConfirm-apiValue', apiValue);

    formValue.current = value;
    const rangeTime = apiValue?.[RANGE_TIME_SELECT_FILTER];
    const regionsValue = apiValue?.[REGION]?.markets;
    // const regionsValue = apiValue?.[REGION]?.value;
    const singleSource = apiValue?.[SINGLE_SOURCE_SELECT_FILTER];
    const storeSegment = apiValue?.[STORERANK_SEGEMENT];
    // console.log('handleConfirm-singleSource', singleSource);
    // console.log('handleConfirm-regionsValue', regionsValue);
    const singleSourceValue = singleSource?.value || 'appstore top free';
    const segmentValue = storeSegment?.value;

    // console.log('handleConfirm-activeSegment', activeSegment);
    // console.log('handleConfirm-segmentValue', segmentValue);

    let filterTmp = {
      regions: regionsValue,
      start_date: rangeTime?.start_date,
      end_date: rangeTime?.end_date,
    } as any;

    if (segmentValue === 'market' && singleSourceValue) {
      filterTmp = {
        ...filterTmp,
        source: [singleSourceValue],
      };
    }
    // console.log('filterTmp', filterTmp);
    onConfirm?.(filterTmp, segmentValue);
    setDetailFilterExtend(apiValue);

    if (activeSegment !== segmentValue) {
      setActiveSegment(segmentValue);
      // setDestroyed(true);
      // setTimeout(() => {
      //   setDestroyed(false);
      // }, 0);
    }

    if (segmentValue === 'rank') {
      updateURLSearchParams({ source: '' }, ['source']);
    }
  };

  const handleReset = () => {
    formValue.current = cloneDeep(DEFAULT_FORM_VALUE);
    resetDetailFilterExtend();
    onReset?.();
  };

  return (
    <Filter
      showButton={false}
      iconText={iconText}
      inputList={inputList}
      defaultValue={DEFAULT_FORM_VALUE}
      value={formValue.current}
      onConfirm={handleConfirm}
      onReset={handleReset}
    />
  );
};

export default StoreRankDetailsFilter;
